import React, { useState, useEffect } from 'react';
import { Row, Col, Progress, Space, Table, Tag } from 'antd';
import {
  FlightDateText,
  PrimaryTableRowText,
  StandardTableRowText,
  TableColumnTitle,
} from '../../../shared/globalStyling/styledText';
import moment from 'moment';
import { formatCurrency2SigFig, formatWholeNumber } from '../../../../core/utils/campaigns';

const Frag = React.Fragment;
const EditorialTable = ({
  data,
  history,
  selectedRowKeys,
  onRowChange,
  location,
  total,
  current,
  pageSize,
}) => {
  const [localPageSize, setLocalPageSize] = useState(10);
  const [localCurrent, setLocalCurrent] = useState(1);

  useEffect(() => {
    const savedPageSize = sessionStorage.getItem('editorialMainPageSize');
    if (savedPageSize) {
      setLocalPageSize(Number(savedPageSize));
    }
  }, []);

  useEffect(() => {
    if (data && data.length > 0) {
      const savedPageSize = Number(sessionStorage.getItem('editorialMainPageSize')) || 10;
      const savedCurrentPage = Number(sessionStorage.getItem('editorialMainCurrentPage')) || 1;

      const maxPage = Math.ceil(data.length / savedPageSize);
      const validPage = savedCurrentPage <= maxPage && savedCurrentPage > 0 ? savedCurrentPage : 1;

      if (validPage !== localCurrent) {
        setLocalCurrent(validPage);
        // Update session storage with the validated page number
        if (validPage !== savedCurrentPage) {
          sessionStorage.setItem('editorialMainCurrentPage', validPage.toString());
        }
      }
    }
  }, [data, localCurrent]);
  const columns = [
    {
      title: <TableColumnTitle text={'Name'} />,
      dataIndex: 'name',
      key: 'name',
      width: '15%',
      align: 'left',

      render: (text) => {
        return (
          <Row type="flex" justify="start">
            <Col>
              <PrimaryTableRowText text={text} />
            </Col>
          </Row>
        );
      },
    },
    {
      title: <TableColumnTitle text={'Status'} />,
      dataIndex: 'status',
      key: 'status',
      width: '15%',
      align: 'left',

      render: (text) => {
        const config = {
          ACTIVE: {
            backgroundColor: '#00DE5133',
            color: '#00DE51',
            text: 'Live',
            width: '50px',
          },
          DEMO: {
            backgroundColor: '#b9b6b633',
            color: '#b9b6b6',
            text: 'Demo',
            width: '60px',
          },
          PAUSED: {
            backgroundColor: 'orange',
            width: '70px',
            color: 'white',
            text: 'Paused',
          },
        };
        const status = text ? text : 'LIVE';
        return (
          <Row type="flex" justify="start">
            <Col>
              <Tag
                style={{
                  ...config[status],
                  border: 0,
                  borderRadius: '25px',
                }}
              >
                <Row justify="space-around" type="flex" align="middle">
                  <div
                    style={{
                      width: '5px',
                      height: '5px',
                      borderRadius: '50px',
                      backgroundColor: config[status].color,
                    }}
                  />
                  {config[status].text}
                </Row>
              </Tag>
            </Col>
          </Row>
        );
      },
    },
    {
      title: <TableColumnTitle text={'Date Range'} />,
      dataIndex: 'startDate',
      key: 'startDate',
      align: 'left',
      width: '14%',
      footerContent: ' ',
      render: (text, record) => {
        if (!text) {
          return <div />;
        }
        const currentDate = moment().format('MM/DD/YY');
        const startDate = record.startDate ? moment(record.startDate) : null;
        const endDate = record.endDate ? moment(record.endDate) : null;
        let flightProgress = 0;
        if (currentDate > endDate) flightProgress = 100;
        else if (currentDate < endDate && currentDate > startDate)
          flightProgress =
            (currentDate.diff(startDate, 'minutes') / endDate.diff(startDate, 'minutes')) * 100;
        return (
          <Frag>
            <Row type="flex" justify="start">
              {record.startDate && (
                <Col>
                  <FlightDateText text={startDate ? startDate.format('MM/DD/YY') : null} />
                </Col>
              )}
              <Col>
                <FlightDateText text={<span>&nbsp; &#8594; &nbsp;</span>} />
              </Col>
              {record.endDate && (
                <Col>
                  <FlightDateText text={endDate ? endDate.format('MM/DD/YY') : null} />
                </Col>
              )}
            </Row>
            <Row type="flex" justify="start">
              <Progress
                id="main-campaign-table-progress"
                type={'line'}
                strokeWidth={3}
                percent={flightProgress ? flightProgress : 0}
                showInfo={false}
                strokeColor={'#636363'}
              />
            </Row>
          </Frag>
        );
      },
    },
    {
      title: <TableColumnTitle text={'Sales'} />,
      dataIndex: 'transactions',
      key: 'transactions',
      align: 'left',
      width: '14%',
      render: (text, record) => {
        return (
          <Col>
            <Row type="flex" justify="start">
              <Space>
                <PrimaryTableRowText text={'Attr. Page Views:'} />
                <StandardTableRowText text={formatWholeNumber(record.clicks)} />
              </Space>
            </Row>
            <Row type="flex" justify="start">
              <Space>
                <PrimaryTableRowText text={'Customers:'} />
                <StandardTableRowText text={formatWholeNumber(record.customers)} />
              </Space>
            </Row>
            <Row type="flex" justify="start">
              <Space>
                <PrimaryTableRowText text={'Transactions:'} />
                <StandardTableRowText text={formatWholeNumber(record.transactions)} />
              </Space>
            </Row>
            <Row type="flex" justify="start">
              <Space>
                <PrimaryTableRowText text={'Revenue:'} />
                <StandardTableRowText text={formatCurrency2SigFig(record.revenue)} />
              </Space>
            </Row>
          </Col>
        );
      },
    },
    {
      title: <TableColumnTitle text={'Sessions'} />,
      dataIndex: 'sessions',
      key: 'sessions',
      align: 'left',
      width: '14%',
      render: (text, record) => {
        return (
          <Col>
            <Row type="flex" justify="start">
              <Space>
                <PrimaryTableRowText text={'Sessions:'} />
                <StandardTableRowText text={formatWholeNumber(record.sessions)} />
              </Space>
            </Row>
            <Row type="flex" justify="start">
              <Space>
                <PrimaryTableRowText text={'Attr. Page Views:'} />
                <StandardTableRowText text={formatWholeNumber(record.sessionAttrPageViews)} />
              </Space>
            </Row>
          </Col>
        );
      },
    },
  ];

  return (
    <div style={{ backgroundColor: '#fff', border: '1px solid #e8e8e8' }}>
      <Table
        onRow={(data) => ({
          onClick: () => {
            const { id } = data;
            history.push(`/publisher/details/${id}/1`);
          },
        })}
        dataSource={data}
        pagination={{
          hideOnSinglePage: false,
          position: ['none', 'bottomCenter'],
          showSizeChanger: true,
          pageSize: localPageSize,
          current: localCurrent,
          total: data ? data.length : 0,
          onChange: (page, size) => {
            setLocalCurrent(page);
            sessionStorage.setItem('editorialMainCurrentPage', page.toString());
            if (size !== localPageSize) {
              setLocalPageSize(size);
              sessionStorage.setItem('editorialMainPageSize', size.toString());
            }
          },
          onShowSizeChange: (current, size) => {
            setLocalPageSize(size);
            setLocalCurrent(1);
            sessionStorage.setItem('editorialMainCurrentPage', '1');
            sessionStorage.setItem('editorialMainPageSize', size.toString());
          },
        }}
        size="default"
        columns={columns}
        scroll={{ x: 1200 }}
        bordered={false}
        rowKey={(record) => record.id}
        rowSelection={{
          onChange: onRowChange,
          selectedRowKeys,
          columnWidth: '5%',
        }}
      />
    </div>
  );
};

export default EditorialTable;
